import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import SectionTitle from "../common/SectionTitle";

/* ================= API CONFIG ================= */
const API_URL =
  "https://ritehomepros.com/marketing_leads_backend/enquiry/create/";
const BUSINESS_ID = "e3896a07-6db2-4e91-a48f-9a25a9d07e9d";

const ContactSection = () => {
  /* ================= STATE ================= */
  const [form, setForm] = useState({
    fname: "",
    lname: "",
    email: "",
    mobile: "",
    service: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ================= VALIDATION ================= */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    if (!form.service) return "Please select a service";
    if (!form.fname.trim()) return "Name is required";
    if (!emailRegex.test(form.email)) return "Enter a valid email";
    if (form.mobile.length !== 10)
      return "Enter a valid 10-digit phone number";
    return null;
  };

  /* ================= HANDLERS ================= */
  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    handleChange("mobile", value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      setSnack({ open: true, message: error, severity: "error" });
      return;
    }

    try {
      setLoading(true);

      await axios.post(API_URL, {
        business: BUSINESS_ID,
        fname: form.fname,
        lname: form.lname || "Customer",
        mobile: form.mobile,
        email: form.email,
        source: "business_website",
        service: form.service,
        message: form.message,
      });

      setSnack({
        open: true,
        message: "Your message has been sent successfully!",
        severity: "success",
      });

      setForm({
        fname: "",
        lname: "",
        email: "",
        mobile: "",
        service: "",
        message: "",
      });
    } catch (error) {
      setSnack({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box id="contact" sx={{ py: { xs: 8, md: 10 } }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: 2 }}>
        <Grid container spacing={4}>
          {/* LEFT CONTENT */}
          <Grid item xs={12} md={6}>
            <SectionTitle
              overline="Get a quote"
              title="Need a Water Restoration Service?"
              subtitle="Send a quick message with your details and preferred schedule. We’ll follow up with a detailed quote."
            />
            <Typography variant="body2" color="text.secondary">
              You can also call us directly at <strong>+1 (888) 835-2473</strong>
            </Typography>
          </Grid>

          {/* FORM */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 1,
                backgroundColor: "#fff",
                boxShadow: "0 12px 35px rgba(15,23,42,0.08)",
              }}
            >
              <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    value={form.fname}
                    onChange={(e) => handleChange("fname", e.target.value)}
                    fullWidth
                    required
                  />

                  <TextField
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    fullWidth
                    required
                  />

                  <TextField
                    label="Phone"
                    value={form.mobile}
                    onChange={handlePhoneChange}
                    fullWidth
                    required
                  />

                  <TextField
                    select
                    label="Service Type"
                    value={form.service}
                    onChange={(e) => handleChange("service", e.target.value)}
                    fullWidth
                    required
                  >
                    <MenuItem value="">Type of service</MenuItem>
                    <MenuItem value="Water Extraction & Structural Drying">
                      Water Extraction & Structural Drying
                    </MenuItem>
                    <MenuItem value="Mold Prevention & Remediation">
                      Mold Prevention & Remediation
                    </MenuItem>
                    <MenuItem value="Sewage Cleanup (Black Water)">
                      Sewage Cleanup (Black Water)
                    </MenuItem>
                    <MenuItem value="Content Cleaning & Pack Out">
                      Content Cleaning & Pack Out
                    </MenuItem>
                    <MenuItem value="Drywall & Flooring Repair">
                      Drywall & Flooring Repair
                    </MenuItem>
                    <MenuItem value="Emergency Roof Tarping & Leak Response">
                      Emergency Roof Tarping & Leak Response
                    </MenuItem>
                  </TextField>

                  <TextField
                    label="Message"
                    multiline
                    minRows={3}
                    value={form.message}
                    onChange={(e) =>
                      handleChange("message", e.target.value)
                    }
                    fullWidth
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Request a quote"}
                  </Button>
                </Stack>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactSection;
